import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { plural } from "@/utils/format";
import {
  dismissNotifications,
  getNotifications,
  type AppNotification,
  type NotificationTone,
} from "@/services/notificationService";

/** Rafraîchissement automatique des alertes (elles sont aussi relues à chaque ouverture de la cloche). */
const REFRESH_MS = 2 * 60 * 1000;

const TONE: Record<NotificationTone, string> = {
  danger: "bg-accent-100 text-accent-700",
  warning: "bg-secondary-100 text-secondary-700",
  info: "bg-primary-100 text-primary-700",
};

interface HeaderNotificationsProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

/**
 * Cloche de l'en-tête : alertes calculées par le serveur selon le rôle et les préférences (Mon compte).
 * Une alerte ouverte disparaît pour cet utilisateur ; elle revient si la situation change.
 */
export default function HeaderNotifications({ open, onToggle, onClose }: HeaderNotificationsProps) {
  const navigate = useNavigate();
  const [items, setItems] = useState<AppNotification[] | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    getNotifications()
      .then((list) => {
        setItems(list);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const count = items?.length ?? 0;

  const go = (link: string) => {
    onClose();
    navigate(link);
  };

  /** Retire tout de suite les alertes de la liste, puis le serveur s'en souvient pour cet utilisateur. */
  const dismiss = (list: AppNotification[]) => {
    if (!list.length) return;
    const types = new Set(list.map((n) => n.type));
    setItems((prev) => (prev ?? []).filter((n) => !types.has(n.type)));
    dismissNotifications(list)
      .then(setItems)
      .catch(() => load());
  };

  return (
    <div className="relative">
      <button
        onClick={() => {
          if (!open) load();
          onToggle();
        }}
        className="press relative w-10 h-10 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
        aria-label={count ? `Notifications : ${plural(count, "alerte")}` : "Notifications"}
      >
        <i className="ri-notification-3-line text-xl"></i>
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-accent-500 text-[0.6875rem] font-semibold leading-[18px] tabular-nums text-white text-center">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="fixed inset-x-3 top-[calc(4.25rem+env(safe-area-inset-top,0px))] max-h-[75dvh] overflow-y-auto overscroll-contain origin-top animate-scale-in rounded-lg border border-background-200 bg-background-50 p-2 shadow-pop sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96 sm:origin-top-right">
          <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">Notifications</p>
          {items === null && !failed && <p className="px-3 py-3 text-xs text-foreground-500">Chargement...</p>}
          {failed && (
            <p className="px-3 py-3 text-xs text-foreground-500">Alertes indisponibles pour le moment.</p>
          )}
          {items?.length === 0 && (
            <p className="flex items-center gap-2 px-3 py-3 text-xs text-foreground-500">
              <i className="ri-checkbox-circle-line text-primary-600"></i> Rien à signaler.
            </p>
          )}
          {items?.map((n) => (
            <button
              key={n.type}
              type="button"
              onClick={() => {
                dismiss([n]);
                go(n.link);
              }}
              className="flex w-full items-start gap-3 rounded-md px-3 py-2 text-left hover:bg-background-100 cursor-pointer"
            >
              <span className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${TONE[n.tone]}`}>
                <i className={`${n.icon} text-base`}></i>
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-medium text-foreground-900 leading-snug">{n.title}</span>
                <span className="block text-xs text-foreground-500 leading-snug">{n.detail}</span>
              </span>
            </button>
          ))}
          <div className="mt-1 flex items-center justify-between border-t border-background-200 pt-1">
            {count > 0 && (
              <button
                type="button"
                onClick={() => dismiss(items ?? [])}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-xs text-foreground-500 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-check-double-line"></i> Tout effacer
              </button>
            )}
            <button
              type="button"
              onClick={() => go("/dashboard/account")}
              className="ml-auto flex items-center gap-2 rounded-md px-3 py-2 text-xs text-foreground-500 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-settings-4-line"></i> Choisir mes alertes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
